import { uniqBy, findIndex } from 'lodash-es';

import {
  getPanels,
  getMultiplePanels,
  duplicatePanel,
  getTags,
} from '../../../services/panels';

const state = {
  panels: [],
  interactiveTags: [],
  lookupTable: {},

  isLoading: false,
  page: 1,
  limit: 20,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  async getPanels(context) {
    const { page, limit, tags, search } = context.state;

    context.commit('SET_PANELS_FIELD', { key: 'isLoading', value: true });

    const { panels, total } = await getPanels({
      page,
      limit,
      tags: tags.join(','),
      search,
    });

    context.commit('SET_PANELS_FIELD', { key: 'total', value: total });
    context.commit('SET_PANELS', panels);
    context.commit('ADD_PANELS_TO_LOOKUP_TABLE', panels);
    context.commit('SET_PANELS_FIELD', { key: 'isLoading', value: false });
  },

  async getInteractiveTags({ commit, rootState }) {
    const { owner } = rootState.production.production;
    const { tags } = await getTags(owner);

    commit('SET_PANELS_FIELD', { key: 'interactiveTags', value: tags });
  },

  /**
   * Pull smart panels from current production's playlist, and fetch just those panels.
   */
  async getTimelinePanels(context) {
    const { production } = context.rootState.production;
    const { timeline } = production;

    const panelIds = timeline.reduce((memo, post) => {
      const panel = context.state.lookupTable[post._id];

      // Ignore if we already have the panel loaded in the lookup table.
      if (post.layout === 'panel' && !panel?.settings) {
        memo.add(post._id);
      }

      // If post is a theme, check for posts that are panels
      if (post?.posts?.length) {
        post.posts.forEach((p) => {
          if (p?.layout === 'panel') {
            memo.add(p._id);
          }
        });
      }

      return memo;
    }, new Set());

    const { panels } = await getMultiplePanels([...panelIds]);
    if (panels?.length) {
      context.commit('ADD_PANELS_TO_LOOKUP_TABLE', panels);
    }
  },

  changeInteractiveTags({ commit, dispatch }, tags) {
    commit('SET_PANELS_FIELD', { key: 'tags', value: tags });

    commit('RESET_PANELS');

    dispatch('getPanels');
  },

  changeInteractiveSearch({ commit, dispatch }, search) {
    commit('SET_PANELS_FIELD', { key: 'search', value: search });

    commit('RESET_PANELS');

    dispatch('getPanels');
  },

  resetInteractiveSearch(context) {
    context.commit('CLEAR_PANELS_TAGS');
    context.commit('SET_PANELS_FIELD', { key: 'search', value: '' });
    context.dispatch('resetPanels');
    context.dispatch('getPanels');
  },

  incrementPanelsPage(context) {
    context.commit('SET_PANELS_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getPanels');
  },

  resetPanels(context) {
    context.commit('RESET_PANELS');
  },

  async duplicatePanel(context, panelId) {
    const panel = await duplicatePanel(panelId);
    context.commit('DUPLICATE_PANEL', panel);
  },

  addToPanelsLookupTable(context, payload) {
    context.commit('ADD_TO_LOOKUP_TABLE', payload);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SET_PANELS(state, panels) {
    state.panels = uniqBy(state.panels.concat(panels), '_id');
  },

  RESET_PANELS(state) {
    state.panels = [];
    state.page = 1;
  },

  ADD_PANELS_TAG(state, tag) {
    state.tags = [...state.tags, tag];
  },

  REMOVE_PANELS_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_PANELS_TAGS(state) {
    state.tags = [];
  },

  /**
   * Merge provided panels into lookupTable.
   * Each panel will be merged with the existing one,
   * if it already exists in state, so we don't clear anything like `settings`.
   */
  ADD_PANELS_TO_LOOKUP_TABLE(state, panels) {
    // Yo dawg, I heard you like spread operators...
    state.lookupTable = {
      ...state.lookupTable,
      ...(panels.reduce((memo, panel) => ({
        ...memo,
        [panel._id]: {
          ...state.lookupTable[panel._id],
          ...panel,
        },
      }), {})),
    };
  },

  SET_PANELS_FIELD(state, { key, value }) {
    state[key] = value;
  },

  DUPLICATE_PANEL(state, payload) {
    state.panels.unshift(payload);
    state.lookupTable[payload._id] = payload;
  },

  EDIT_PANEL(state, payload) {
    const index = findIndex(state.panels, (o) => o._id === payload._id);
    state.panels.splice(index, 1, payload);
    state.lookupTable[payload._id] = payload;
  },

  ADD_TO_LOOKUP_TABLE(state, payload) {
    // Merge with any existing data we have on panel,
    // so we don't override settings or anything if they were already loaded.
    state.lookupTable[payload._id] = {
      ...state.lookupTable[payload._id],
      ...payload,
    };

    // Set to trigger state update + rerendering where necessary
    state.lookupTable = { ...state.lookupTable };
  },
};

const getters = {
  hasMoreSmartPanels(state) {
    return state.total > (state.page * state.limit);
  },

  sortedInteractiveTags(state) {
    return state.interactiveTags.sort();
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
